"use client";

import { useCallback } from "react";
import type { Locale } from "@/lib/i18n";

type GoogleFcQueueEntry = () => void;

type GoogleFcWindow = {
  googlefc?: {
    callbackQueue?: GoogleFcQueueEntry[];
    showRevocationMessage?: () => void;
  };
};

const COPY: Record<Locale, { label: string; fallback: string }> = {
  ko: {
    label: "개인정보 옵션 관리",
    fallback:
      "동의 관리 화면을 불러오지 못했어요. 페이지를 새로고침하거나 잠시 후 다시 시도해 주세요.",
  },
  en: {
    label: "Manage Privacy Choices",
    fallback:
      "Could not open the consent preference center. Please refresh the page or try again shortly.",
  },
};

function openConsentRevocation(fallbackMessage: string) {
  const win = window as Window & GoogleFcWindow;
  const googlefc = win.googlefc;
  if (!googlefc) {
    window.alert(fallbackMessage);
    return;
  }
  if (typeof googlefc.showRevocationMessage === "function") {
    googlefc.showRevocationMessage();
    return;
  }
  googlefc.callbackQueue = googlefc.callbackQueue || [];
  googlefc.callbackQueue.push(() => {
    googlefc.showRevocationMessage?.();
  });
}

export function ManagePrivacyChoicesButton({
  locale = "en",
  className,
  label,
}: {
  locale?: Locale;
  className?: string;
  label?: string;
}) {
  const copy = COPY[locale];

  const openPrivacyChoices = useCallback(() => {
    openConsentRevocation(copy.fallback);
  }, [copy.fallback]);

  return (
    <button
      type="button"
      onClick={openPrivacyChoices}
      className={
        className ??
        "inline-flex items-center justify-center rounded-full border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700 transition hover:border-brand-400 hover:text-brand-600"
      }
    >
      {label ?? copy.label}
    </button>
  );
}

export function DoNotSellLink() {
  const openDoNotSellChoices = useCallback(() => {
    openConsentRevocation(COPY.en.fallback);
  }, []);

  return (
    <button
      type="button"
      onClick={openDoNotSellChoices}
      className="text-[11px] text-slate-500 underline-offset-2 hover:text-slate-700 hover:underline"
    >
      Do Not Sell or Share My Personal Information
    </button>
  );
}
